// Synchronizace školního kalendáře — Graph volání přes backend proxy

import { CalendarEvent } from './icsCalendar'
import { TIMEOUTS } from './constants'

const API_BASE = window.location.origin + '/api'

async function createCalendarEvent(event: CalendarEvent): Promise<boolean> {
  try {
    const graphEvent: Record<string, unknown> = {
      subject: event.subject || 'Bez názvu',
      start: {
        dateTime: event.start.dateTime,
        timeZone: event.start.timeZone || 'UTC',
      },
      end: {
        dateTime: event.end.dateTime,
        timeZone: event.end.timeZone || 'UTC',
      },
    }

    if (event.location?.displayName) {
      graphEvent.location = { displayName: event.location.displayName }
    }

    if (event.body?.content) {
      let contentType = 'text'
      if (event.body.contentType?.includes('html')) {
        contentType = 'html'
      }
      graphEvent.body = {
        contentType,
        content: event.body.content,
      }
    }

    const response = await fetch(`${API_BASE}/graph-calendar/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(graphEvent),
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Error creating calendar event:', response.status, errorText)
      try {
        const errorJson = JSON.parse(errorText)
        if (
          errorJson.error?.code === 'Authorization_RequestDenied' ||
          response.status === 403
        ) {
          throw new Error(
            'Nemáte oprávnění k zápisu do kalendáře. Je potřeba oprávnění Calendars.ReadWrite v Azure AD.'
          )
        }
      } catch (e: unknown) {
        if (e instanceof Error && e.message.includes('oprávnění')) {
          throw e
        }
      }
      return false
    }

    return true
  } catch (error) {
    console.error('Error creating calendar event:', error)
    return false
  }
}

export async function syncSchoolCalendarToUser(events: CalendarEvent[]): Promise<{
  success: number
  failed: number
  errors: string[]
}> {
  const results = { success: 0, failed: 0, errors: [] as string[] }

  for (const event of events) {
    try {
      const success = await createCalendarEvent(event)
      if (success) {
        results.success++
      } else {
        results.failed++
        results.errors.push(`Nepodařilo se přidat: ${event.subject}`)
      }
      await new Promise((resolve) => setTimeout(resolve, TIMEOUTS.CALENDAR_SYNC_DELAY))
    } catch (error: unknown) {
      results.failed++
      const msg = error instanceof Error ? error.message : String(error)
      results.errors.push(`Chyba při přidávání "${event.subject}": ${msg}`)
    }
  }

  return results
}

async function eventExists(event: CalendarEvent): Promise<boolean> {
  try {
    const startDate = new Date(event.start.dateTime)
    const endDate = new Date(event.end.dateTime)
    const searchStart = new Date(startDate.getTime() - 3600000).toISOString()
    const searchEnd = new Date(endDate.getTime() + 3600000).toISOString()

    const params = new URLSearchParams({
      startDateTime: searchStart,
      endDateTime: searchEnd,
    })

    const response = await fetch(`${API_BASE}/graph-calendar/calendarView?${params}`, {
      credentials: 'include',
    })

    if (!response.ok) return false

    const data = await response.json()
    const existingEvents = data.value || []

    return existingEvents.some((existing: { subject?: string; start?: { dateTime?: string } }) => {
      const existingStart = new Date(existing.start?.dateTime || 0)
      const eventStart = new Date(event.start.dateTime)
      const timeDiff = Math.abs(existingStart.getTime() - eventStart.getTime())
      return existing.subject === event.subject && timeDiff < 1800000
    })
  } catch (error) {
    console.error('Error checking if event exists:', error)
    return false
  }
}

export async function syncSchoolCalendarToUserSmart(events: CalendarEvent[]): Promise<{
  success: number
  skipped: number
  failed: number
  errors: string[]
}> {
  const results = { success: 0, skipped: 0, failed: 0, errors: [] as string[] }

  for (const event of events) {
    try {
      const exists = await eventExists(event)
      if (exists) {
        results.skipped++
        continue
      }

      const success = await createCalendarEvent(event)
      if (success) {
        results.success++
      } else {
        results.failed++
        if (results.errors.length === 0) {
          results.errors.push(
            'Nepodařilo se přidat události. Zkontrolujte oprávnění Calendars.ReadWrite v Azure AD.'
          )
        }
      }

      await new Promise((resolve) => setTimeout(resolve, TIMEOUTS.CALENDAR_SYNC_ERROR_DELAY))
    } catch (error: unknown) {
      results.failed++
      const errorMsg = error instanceof Error ? error.message : String(error)
      if (
        errorMsg.includes('oprávnění') ||
        errorMsg.includes('permission') ||
        errorMsg.includes('authorization')
      ) {
        if (!results.errors.some((e) => e.includes('oprávnění') || e.includes('permission'))) {
          results.errors.push(
            'Nemáte oprávnění k zápisu do kalendáře. Je potřeba oprávnění Calendars.ReadWrite v Azure AD.'
          )
        }
        break
      }
      results.errors.push(errorMsg)
    }
  }

  return results
}

export async function grantCalendarReadPermission(): Promise<{
  success: boolean
  message: string
}> {
  const icsUrl =
    'https://outlook.office365.com/owa/calendar/b86242d5bf2d4496a89c04f9eaf4cc69@iss-cheb.cz/6d0809beabcb451898dddfc17196b3433773793055790110233/calendar.ics'

  localStorage.setItem('calendar_subscription', 'true')
  localStorage.setItem('calendar_subscription_date', new Date().toISOString())
  localStorage.setItem('calendar_ics_url', icsUrl)

  const webcalUrl = icsUrl.replace('https://', 'webcal://')

  try {
    window.location.href = webcalUrl
    return {
      success: true,
      message:
        'Kalendář se otevírá v Outlook. Outlook automaticky navrhne přidání kalendáře do vašeho seznamu kalendářů.',
    }
  } catch {
    window.open(icsUrl, '_blank')
    return {
      success: true,
      message:
        'Kalendář se otevírá. Pokud se Outlook neotevřel automaticky, otevřete stažený soubor .ics.',
    }
  }
}
